import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import PatientPasswordLifecyclePanel from "@/pages/profile-settings/patient/PatientPasswordLifecyclePanel.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import { recordLegalAcceptance } from "@/services/legalAcceptanceService.js";
import { advanceStagingFirstUse } from "@/services/stagingAccessService.js";
import supabase from "@/services/supabaseClient.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

export default function ChangePassword({
  intent = "auto",
  firstUseReturnTo = "",
  firstUseNextStage = "category",
  firstUseAdvance = true,
  firstUseSignOutAfterCreate = true,
  firstUseLoginNext = "",
  loginSecurityPathOverride = "",
}) {
  const nav = useNavigate();
  const location = useLocation();
  const settingsRole = location.pathname.startsWith("/settings/doctor") ? "doctor" : "patient";
  const loginSecurityPath = loginSecurityPathOverride || `/settings/${settingsRole}/login-security`;
  const returnTo = String(location.state?.returnTo || firstUseReturnTo || "").trim();
  const returnState = location.state?.returnState;
  const firstUse =
    location.state?.firstUse === true ||
    new URLSearchParams(location.search || "").get("first_use") === "1" ||
    Boolean(firstUseReturnTo);
  const [legalAccepted, setLegalAccepted] = React.useState(false);
  const { t } = useLanguage();

  function handleClose() {
    if (returnTo) {
      nav(returnTo, returnState ? { state: returnState } : undefined);
      return;
    }
    nav(loginSecurityPath);
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter close onBack={handleClose} />
      <PatientPasswordLifecyclePanel
        className="mt-5"
        titleClassName="text-title-large text-on-surface"
        intent={intent}
        resetBasePath={`${loginSecurityPath}/change-password`}
        loginSecurityPath={loginSecurityPath}
        preserveAccountSessionAfterUpdate={firstUse || (settingsRole === "doctor" && intent !== "create")}
        canSubmitExtra={!firstUse || legalAccepted}
        trailingFormContent={
          firstUse ? (
            <div className="flex items-center gap-3 text-body-small text-on-surface-var">
              <Checkbox
                checked={legalAccepted}
                onChange={setLegalAccepted}
                ariaLabel={t("firstUse.password.agreeLabel", "Agree to the Terms & Conditions and Privacy Policy")}
                className="shrink-0"
              />
              <span>
                {t("firstUse.password.agreePrefix", "I agree to the")} <a className="text-primary underline" href="/legal/terms" target="_blank" rel="noreferrer">{t("legal.terms.title", "Terms & Conditions")}</a> {t("firstUse.password.and", "and")} <a className="text-primary underline" href="/legal/privacy" target="_blank" rel="noreferrer">{t("legal.privacy.title", "Privacy Policy")}</a>.
              </span>
            </div>
          ) : null
        }
        onCreatePasswordSuccess={async ({ user }) => {
          if (firstUse && user?.id) {
            await recordLegalAcceptance({
              userId: user.id,
              sourceRoute: returnTo || "/onboarding?first_use=1",
              accepted: true,
            });
            if (firstUseAdvance) {
              await advanceStagingFirstUse(firstUseNextStage);
            }
            if (!firstUseSignOutAfterCreate) {
              if (returnTo) nav(returnTo, { replace: true, ...(returnState ? { state: returnState } : {}) });
              return;
            }
            try {
              await supabase.auth.signOut({ scope: "local" });
            } finally {
              const safeLoginNext =
                String(firstUseLoginNext || "").startsWith("/") &&
                !String(firstUseLoginNext || "").startsWith("//")
                  ? String(firstUseLoginNext)
                  : "";
              if (safeLoginNext) {
                nav(`/login?next=${encodeURIComponent(safeLoginNext)}`, {
                  replace: true,
                  state: { firstUsePasswordCreated: true, returnTo },
                });
                return;
              }
              nav("/login", {
                replace: true,
                state: { firstUsePasswordCreated: true, returnTo },
              });
            }
            return;
          }
          if (!returnTo) return;
          nav(returnTo, { replace: true, ...(returnState ? { state: returnState } : {}) });
        }}
        onCreated={() => {
          if (!returnTo) return;
          window.setTimeout(() => {
            nav(returnTo, returnState ? { state: returnState } : undefined);
          }, 2200);
        }}
      />
    </SettingsScreenShell>
  );
}
